import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchPublicPlans, fetchSubscription } from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import type { Plan, Subscription } from '../types';
import { pricingPlans } from '../lib/marketing';

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const fallbackPlans: Plan[] = pricingPlans.map((plan) => ({
  id: plan.name,
  slug: plan.price === 'Free trial'
    ? 'trial'
    : plan.name === 'Enterprise'
    ? 'custom'
    : plan.name.toLowerCase(),
  name: plan.name,
  description: plan.description,
  priceMonthly: plan.price === 'Free trial' || plan.price === 'Talk to us' ? null : Number(plan.price.replace(/[^0-9.]/g, '')),
  currency: 'usd',
  reportLimit: undefined,
  features: plan.perks,
  stripePriceId: null,
  trialDays: plan.price === 'Free trial' ? 7 : 0,
  isPublic: true,
  isCustom: plan.name === 'Enterprise',
  organizationId: null,
  active: true,
})) as Plan[];

export function PricingPage(): JSX.Element {
  const { user } = useAuth();
  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['public-plans'],
    queryFn: fetchPublicPlans,
    staleTime: 1000 * 60 * 60,
  });

  const { data: subscription } = useQuery<Subscription | undefined>({
    queryKey: ['subscription', user?.organizationId],
    queryFn: async () => {
      if (!user) return undefined;
      return fetchSubscription(user.organizationId);
    },
    enabled: Boolean(user?.organizationId),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const displayedPlans = useMemo<Plan[]>(() => {
    if (plans.length > 0) {
      return plans;
    }
    return fallbackPlans;
  }, [plans]);

  return (
    <div className="space-y-12">
      <header className="space-y-4 text-center">
        <span className="badge-soft">Memberships</span>
        <h1 className="text-4xl font-semibold text-foreground">Choose the InspectAI experience that fits your brand</h1>
        <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
          Begin with a free export to feel the polish. Upgrade for richer collaboration, concierge support, and integrations that keep your portfolio humming.
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-3">
        {isLoading && plans.length === 0
          ? [...Array(3).keys()].map((key) => <div key={key} className="glass-card h-64 animate-pulse" />)
          : displayedPlans.map((plan) => {
              const isCurrent = subscription ? subscription.plan === plan.slug : false;
              const priceLabel =
                plan.priceMonthly != null ? `${currencyFormatter.format(plan.priceMonthly)} / mo` : plan.isCustom ? 'Custom pricing' : 'Free';
              const isCustom = plan.isCustom || plan.priceMonthly == null;
              const isTrial = plan.slug === 'trial' || plan.slug === 'starter';
              const ctaLabel = isCurrent ? 'Current plan' : isCustom ? 'Talk to us' : isTrial ? 'Start for free' : 'Upgrade to Pro';
              const features = plan.features ?? [];
              const targetHref = isCurrent
                ? '/app/billing'
                : user && !isCustom
                ? '/app/billing'
                : isTrial
                ? '/signup'
                : `/signup?plan=${plan.slug}`;
              return (
                <div
                  key={plan.id}
                  className="glass-card flex h-full flex-col gap-4 p-6"
                >
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{plan.name}</p>
                    <h2 className="text-2xl font-semibold text-foreground">{priceLabel}</h2>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  </div>
                  {isCurrent && (
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                      Current plan
                    </span>
                  )}
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {features.map((feature) => (
                      <li key={feature}>• {feature}</li>
                    ))}
                  </ul>
                  {isCustom ? (
                    <a
                      href="mailto:raykundan57@gmail.com"
                      target="_blank"
                      rel="noreferrer"
                      className="focus-ring mt-auto inline-flex items-center justify-center rounded-full border border-primary/40 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10"
                    >
                      {ctaLabel}
                    </a>
                  ) : (
                    <Link
                      to={targetHref}
                      className="focus-ring mt-auto inline-flex items-center justify-center rounded-full border border-primary/40 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10"
                    >
                      {ctaLabel}
                    </Link>
                  )}
                </div>
              );
            })}
      </div>

      <section className="glass-card space-y-3 p-6 text-sm text-muted-foreground">
        <h2 className="text-lg font-semibold text-foreground">Billing capabilities</h2>
        <ul className="grid gap-2 sm:grid-cols-2">
          <li>Stripe-hosted checkout and customer portal for upgrades and cancellations</li>
          <li>Metered usage tracking for report exports and PDF generation</li>
          <li>Annual billing discounts with automatic proration</li>
          <li>GST, VAT, and tax invoices handled by Stripe</li>
        </ul>
      </section>
    </div>
  );
}
